import React, { useState } from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { FaMinus, FaPlus } from "react-icons/fa";

const StepsContainer = styled.div`
  margin: 0 auto;
  padding: 2rem 0;
  display: flex;
  gap: 20px;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const Title = styled.h2`
  font-size: 2rem;
  color: #102a8e;
  margin-bottom: 2rem;
  font-weight: bold;
  width: 538px;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const StepAccordion = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 658px;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const StepItem = styled.div``;

interface StepButtonProps {
  isOpen: boolean;
}

const StepButton = styled.button<StepButtonProps>`
  width: 100%;
  text-align: left;
  padding: 1rem 2rem;
  background: #fafafa;
  border-radius: 10px;
  border: none;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #102a8e;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  font-family: "Manrope", sans-serif;

  svg {
    width: 10px;
    height: 10px;
    color: #333333;
    transition: transform 0.3s ease;
  }
`;

const StepNumber = styled.span`
  color: #5575f7;
  margin-right: 1rem;
  font-weight: 600;
`;

const StepContent = styled(motion.div)`
  padding: 1.5rem 2rem;
  color: #131313;
  font-size: 14px;
  line-height: 1.6;
  font-family: "Manrope", sans-serif;
  font-weight: 400;
`;

export const Steps = () => {
  const [openStep, setOpenStep] = useState<number | null>(0);

  const steps = [
    {
      id: 1,
      title: "Determine your Eligibility",
      content:
        "Before diving into the registration process, it's important to determine if you meet the right eligibility requirements to get HCPC registration. HCPC regulates multiple range of professions in the UK, including physiotherapists, occupational therapists, and radiologists. Visit the 'Check the Register' to confirm your profession falls under HCPC regulation and view specific requirements.",
    },
    {
      id: 2,
      title: "Gather Required Documentation",
      content:
        "Collect all necessary documents including your professional qualifications, identification, and work experience records.",
    },
    {
      id: 3,
      title: "Complete the Application Form",
      content:
        "Fill out the comprehensive HCPC application form with your personal and professional details.",
    },
    {
      id: 4,
      title: "Pay the Application Fee",
      content:
        "Submit the required registration fee according to your professional category.",
    },
    {
      id: 5,
      title: "Submit Supporting Documents",
      content:
        "Send all your supporting documentation along with your completed application.",
    },
    {
      id: 6,
      title: "Await the Decision",
      content:
        "HCPC will review your application and supporting documents before making a decision.",
    },
    {
      id: 7,
      title: "Respond to Additional Requests",
      content:
        "Be prepared to provide any additional information if requested by HCPC.",
    },
    {
      id: 8,
      title: "Confirmation of Registration",
      content:
        "Once approved, you'll receive confirmation of your HCPC registration.",
    },
  ];

  const toggleStep = (id: number) => {
    setOpenStep(openStep === id ? null : id);
  };

  return (
    <StepsContainer>
      <Title>Step-by-Step Guide to Getting Registered with HCPC UK</Title>
      <StepAccordion>
        {steps.map((step) => (
          <StepItem key={step.id}>
            <StepButton
              onClick={() => toggleStep(step.id)}
              isOpen={openStep === step.id}
            >
              <div>
                <StepNumber>{step.id}.</StepNumber>
                {step.title}
              </div>
              {openStep === step.id ? <FaMinus /> : <FaPlus />}
            </StepButton>
            <AnimatePresence>
              {openStep === step.id && (
                <StepContent
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  {step.content}
                </StepContent>
              )}
            </AnimatePresence>
          </StepItem>
        ))}
      </StepAccordion>
    </StepsContainer>
  );
};
